import { useState, useEffect, useCallback, type ChangeEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { fi } from 'date-fns/locale'

import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { useOrganization } from '../hooks/useOrganization'
import { apiClient } from '../api/client'

interface JournalEntry {
  id: string
  date: string
  voucherNumber: number | null
  description: string | null
  accountCode: string
  accountName: string
  debit: number
  credit: number
  transactionId: string | null
  status: string
}

interface Account {
  code: string
  name: string
}

interface JournalResponse {
  entries: JournalEntry[]
  total: number
  limit: number
  offset: number
  accounts: Account[]
}

export default function JournalPage() {
  const { t } = useTranslation()
  const { currentOrganization } = useOrganization()
  const [searchParams, setSearchParams] = useSearchParams()

  // Filter states
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '')
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '')
  const [accountId, setAccountId] = useState(searchParams.get('accountId') || '')
  const [voucherFrom, setVoucherFrom] = useState(searchParams.get('voucherFrom') || '')
  const [voucherTo, setVoucherTo] = useState(searchParams.get('voucherTo') || '')

  // Data states
  const [entries, setEntries] = useState<JournalEntry[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [limit] = useState(100)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchJournal = useCallback(async () => {
    if (!currentOrganization) return

    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      params.set('organization_id', currentOrganization.id)
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      if (accountId) params.set('accountId', accountId)
      if (voucherFrom) params.set('voucherFrom', voucherFrom)
      if (voucherTo) params.set('voucherTo', voucherTo)
      params.set('limit', limit.toString())
      params.set('offset', offset.toString())

      const response = await apiClient.get<JournalResponse>(`/journal?${params.toString()}`)
      setEntries(response.entries)
      setAccounts(response.accounts)
      setTotal(response.total)

      // Update URL params
      setSearchParams(params, { replace: true })
    } catch {
      setError(t('journal.errorDescription') || 'Failed to load journal entries')
    } finally {
      setLoading(false)
    }
  }, [currentOrganization, startDate, endDate, accountId, voucherFrom, voucherTo, offset, limit, setSearchParams, t])

  useEffect(() => {
    fetchJournal()
  }, [fetchJournal])

  const handleExport = async (format: 'pdf' | 'xlsx') => {
    if (!currentOrganization) return

    setExporting(format)
    setError(null)
    try {
      const params = new URLSearchParams()
      params.set('organization_id', currentOrganization.id)
      params.set('format', format)
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      if (accountId) params.set('accountId', accountId)
      if (voucherFrom) params.set('voucherFrom', voucherFrom)
      if (voucherTo) params.set('voucherTo', voucherTo)

      // Use fetch directly for blob download
      const API_URL = import.meta.env.VITE_API_URL || 'https://kirjuri-backend.tuomas-508.workers.dev'
      const response = await fetch(`${API_URL}/journal/export?${params.toString()}`, {
        credentials: 'include',
        headers: {
          'Accept': format === 'pdf' ? 'text/html' : 'text/csv',
        }
      })

      if (!response.ok) {
        throw new Error('Export failed')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `paivakirja_${startDate || 'all'}_${endDate || 'all'}.${format === 'pdf' ? 'html' : 'csv'}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      setError(t('journal.exportErrorDescription') || 'Export failed')
    } finally {
      setExporting(null)
    }
  }

  const handleReset = () => {
    setStartDate('')
    setEndDate('')
    setAccountId('')
    setVoucherFrom('')
    setVoucherTo('')
    setOffset(0)
  }

  const handlePrevPage = () => {
    setOffset(Math.max(0, offset - limit))
  }

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit)
    }
  }

  const totalDebit = entries.reduce((sum, e) => sum + e.debit, 0)
  const totalCredit = entries.reduce((sum, e) => sum + e.credit, 0)

  const formatDate = (date: string) => {
    try {
      return format(new Date(date), 'd.M.yyyy', { locale: fi })
    } catch {
      return date
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount)
  }

  const handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)
  const handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)
  const handleVoucherFromChange = (e: ChangeEvent<HTMLInputElement>) => setVoucherFrom(e.target.value)
  const handleVoucherToChange = (e: ChangeEvent<HTMLInputElement>) => setVoucherTo(e.target.value)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">{t('journal.title') || 'Päiväkirja'}</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null || entries.length === 0}
          >
            <span className="mr-2">📄</span>
            {exporting === 'pdf' ? (t('common.loading') || 'Ladataan...') : (t('journal.exportPdf') || 'Vie PDF')}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null || entries.length === 0}
          >
            <span className="mr-2">📊</span>
            {exporting === 'xlsx' ? (t('common.loading') || 'Ladataan...') : (t('journal.exportExcel') || 'Vie Excel')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters */}
      <Card title={t('journal.filters') || 'Suodattimet'}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="space-y-2">
            <label htmlFor="startDate" className="text-sm font-medium">{t('journal.startDate') || 'Alkupäivä'}</label>
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="endDate" className="text-sm font-medium">{t('journal.endDate') || 'Loppupäivä'}</label>
            <Input
              id="endDate"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="account" className="text-sm font-medium">{t('journal.account') || 'Tili'}</label>
            <select
              id="account"
              value={accountId}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setAccountId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('journal.allAccounts') || 'Kaikki tilit'}</option>
              {accounts.map((acc) => (
                <option key={acc.code} value={acc.code}>
                  {acc.code} - {acc.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label htmlFor="voucherFrom" className="text-sm font-medium">{t('journal.voucherFrom') || 'Tosite alkaen'}</label>
            <Input
              id="voucherFrom"
              type="number"
              placeholder="1"
              value={voucherFrom}
              onChange={handleVoucherFromChange}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="voucherTo" className="text-sm font-medium">{t('journal.voucherTo') || 'Tosite loppuen'}</label>
            <Input
              id="voucherTo"
              type="number"
              placeholder="9999"
              value={voucherTo}
              onChange={handleVoucherToChange}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={handleReset}>
            <span className="mr-2">🔄</span>
            {t('common.reset') || 'Tyhjennä'}
          </Button>
          <Button onClick={() => { setOffset(0); fetchJournal(); }} disabled={loading}>
            {loading ? (t('common.loading') || 'Ladataan...') : (t('common.search') || 'Hae')}
          </Button>
        </div>
      </Card>

      {/* Results */}
      <Card
        title={`${t('journal.entries') || 'Kirjaukset'} (${total})`}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={offset === 0 || loading}
            >
              <span>◀</span>
            </Button>
            <span className="text-sm text-gray-500">
              {offset + 1} - {Math.min(offset + limit, total)} / {total}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={offset + limit >= total || loading}
            >
              <span>▶</span>
            </Button>
          </div>
        }
      >
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('journal.date') || 'Päivämäärä'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('journal.voucherNumber') || 'Tosite'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('journal.account') || 'Tili'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('journal.description') || 'Selite'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('journal.debit') || 'Debet'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('journal.credit') || 'Kredit'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('journal.statusLabel') || 'Tila'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    {t('journal.noEntries') || 'Ei kirjauksia'}
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(entry.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.transactionId ? (
                        <a
                          href={`/transactions?highlight=${entry.transactionId}`}
                          className="text-blue-600 hover:underline"
                          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.preventDefault()
                            window.location.href = `/transactions?highlight=${entry.transactionId}`
                          }}
                        >
                          {entry.voucherNumber || '-'}
                        </a>
                      ) : (
                        entry.voucherNumber || '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-medium">{entry.accountCode}</span>
                      <span className="text-gray-500 ml-2">{entry.accountName}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {entry.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <Badge variant={entry.status === 'approved' ? 'success' : 'default'}>
                        {entry.status}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
              {entries.length > 0 && (
                <tr className="bg-gray-50 font-bold">
                  <td colSpan={4} className="px-6 py-4 text-right text-sm text-gray-900">
                    {t('journal.total') || 'Yhteensä'}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-900">
                    {formatCurrency(totalDebit)}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-900">
                    {formatCurrency(totalCredit)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
